import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Check, Instagram, Pin } from "lucide-react";

const CopyBtn = ({ value, label, testId }) => {
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handle}
      className="inline-flex items-center gap-1.5 bg-[#25a1c2] hover:bg-[#068fa2] text-white text-xs font-bold rounded-full px-4 py-2 transition-all"
      data-testid={testId}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied!" : label}
    </button>
  );
};

// Reusable square IG post wrapper
const Card1to1 = ({ children, id }) => (
  <div
    id={id}
    data-testid={id}
    className="w-full aspect-square relative overflow-hidden rounded-2xl shadow-2xl"
    style={{ background: "linear-gradient(135deg, #042F2E 0%, #25a1c2 50%, #068fa2 100%)" }}
  >
    <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#5EEAD4]/20 rounded-full blur-3xl"></div>
    <div className="absolute -bottom-32 -left-20 w-72 h-72 bg-[#CCFBF1]/15 rounded-full blur-3xl"></div>
    <div className="relative h-full flex flex-col justify-between p-8 md:p-10 text-white">{children}</div>
  </div>
);

// Reusable 4:5 portrait wrapper (carousel optimal)
const Card4x5 = ({ children, id }) => (
  <div
    id={id}
    data-testid={id}
    className="w-full aspect-[4/5] relative overflow-hidden rounded-2xl shadow-2xl"
    style={{ background: "linear-gradient(135deg, #042F2E 0%, #25a1c2 50%, #068fa2 100%)" }}
  >
    <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#5EEAD4]/20 rounded-full blur-3xl"></div>
    <div className="absolute -bottom-32 -left-20 w-72 h-72 bg-[#CCFBF1]/15 rounded-full blur-3xl"></div>
    <div className="relative h-full flex flex-col justify-between p-8 text-white">{children}</div>
  </div>
);

const BrandFooter = ({ small }) => (
  <div className="text-center">
    <p className={`font-sans ${small ? "text-[9px]" : "text-[10px]"} tracking-[0.3em] uppercase text-[#5EEAD4] font-bold`}>
      Mind Body Spirit HYGIENE™
    </p>
    <p className={`font-sans ${small ? "text-[9px]" : "text-[10px]"} text-white/60 mt-1`}>@mindbodyspirithygienetools</p>
  </div>
);

// ====== POST 1 — PATTERN INTERRUPT ======
const Post1Image = () => (
  <Card1to1 id="post-1-image">
    <div>
      <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold mb-2">Pattern Interrupt</p>
    </div>
    <div className="text-center">
      <h2 className="font-serif text-3xl md:text-4xl leading-[1.1] tracking-tight">
        If you reach for your phone <span className="text-[#5EEAD4] italic">before</span> you reach for your own heart —
      </h2>
      <div className="w-12 h-[2px] bg-[#5EEAD4] mx-auto my-6"></div>
      <p className="font-serif italic text-xl md:text-2xl text-[#CCFBF1]">this post is for you.</p>
    </div>
    <BrandFooter />
  </Card1to1>
);

// ====== POST 2 — LISTICLE ======
const Post2Image = () => (
  <Card1to1 id="post-2-image">
    <div>
      <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold">5 Signs You Are Not Tapped-In</p>
    </div>
    <div className="space-y-3">
      {[
        "You apologize before you speak",
        "Your 'no' has 5 sentences attached",
        "You wake up exhausted before you stand up",
        "You forgot what YOU want",
        "You're performing your life — not living it",
      ].map((line, i) => (
        <div key={i} className="flex items-start gap-3">
          <span className="font-serif text-2xl text-[#5EEAD4] leading-none flex-shrink-0">{i + 1}</span>
          <p className="font-serif text-base md:text-lg leading-snug text-white">{line}</p>
        </div>
      ))}
    </div>
    <div className="text-center pt-4">
      <p className="font-serif italic text-[#5EEAD4] text-sm mb-2">Comment CARDS for the free deck 💛</p>
      <BrandFooter small />
    </div>
  </Card1to1>
);

// ====== POST 3 — VULNERABLE STORY ======
const Post3Image = () => (
  <Card1to1 id="post-3-image">
    <div>
      <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold">A True Story</p>
    </div>
    <div className="text-center">
      <p className="font-serif italic text-xl md:text-2xl text-[#CCFBF1] leading-relaxed mb-6">
        "The pillow hears my screams.
        <br />
        And wipes my tears."
      </p>
      <div className="w-10 h-[2px] bg-[#5EEAD4] mx-auto mb-6"></div>
      <p className="font-serif text-lg md:text-xl text-white leading-snug">
        That was the morning I stopped looking <span className="italic">outside</span> myself for the answer.
      </p>
    </div>
    <div className="text-center">
      <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#5EEAD4] font-bold mb-2">Full 9-min audio · link in bio</p>
      <BrandFooter small />
    </div>
  </Card1to1>
);

// ====== POST 4 — REFRAME ======
const Post4Image = () => (
  <Card1to1 id="post-4-image">
    <div>
      <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold">The Reframe</p>
    </div>
    <div className="text-center space-y-6">
      <div>
        <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#FCA5A5] font-bold mb-2">Stop saying</p>
        <p className="font-serif text-2xl md:text-3xl text-white/70 line-through decoration-[#FCA5A5]/60">
          "I have to be productive."
        </p>
      </div>
      <div className="w-16 h-[2px] bg-[#5EEAD4] mx-auto"></div>
      <div>
        <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#5EEAD4] font-bold mb-2">Start LIVING</p>
        <p className="font-serif text-2xl md:text-3xl text-white">
          "I GET TO <span className="italic text-[#5EEAD4]">Tap In.</span>"
        </p>
      </div>
    </div>
    <BrandFooter />
  </Card1to1>
);

// ====== POST 5 — CAROUSEL TEACHING (7 slides) ======
const Post5Slides = [
  // Slide 1 — Hook
  (
    <Card4x5 id="post-5-slide-1" key="s1">
      <div>
        <p className="font-sans text-[9px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold">Free Practice · Save This</p>
      </div>
      <div className="text-center">
        <h2 className="font-serif text-3xl md:text-4xl leading-[1.05] tracking-tight mb-4">
          How to come home to yourself in <span className="text-[#5EEAD4]">60 seconds.</span>
        </h2>
        <p className="font-serif italic text-base text-[#CCFBF1] mt-4">→ swipe</p>
      </div>
      <BrandFooter small />
    </Card4x5>
  ),
  // Slide 2 — Tap In
  (
    <Card4x5 id="post-5-slide-2" key="s2">
      <p className="font-sans text-[9px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold">Step 1 / 5</p>
      <div className="text-center">
        <p className="font-serif text-7xl text-[#5EEAD4] mb-4">1.</p>
        <h3 className="font-serif text-3xl md:text-4xl mb-3">TAP IN.</h3>
        <p className="font-sans text-sm text-white/80 leading-relaxed">
          Hand on your chest. Or your belly. Somewhere that says: <em>I'm here.</em>
        </p>
      </div>
      <BrandFooter small />
    </Card4x5>
  ),
  // Slide 3 — Ground
  (
    <Card4x5 id="post-5-slide-3" key="s3">
      <p className="font-sans text-[9px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold">Step 2 / 5</p>
      <div className="text-center">
        <p className="font-serif text-7xl text-[#5EEAD4] mb-4">2.</p>
        <h3 className="font-serif text-3xl md:text-4xl mb-3">GROUND.</h3>
        <p className="font-sans text-sm text-white/80 leading-relaxed">
          Feel your feet. Through your socks, the floor, the earth that holds you.
        </p>
      </div>
      <BrandFooter small />
    </Card4x5>
  ),
  // Slide 4 — Say Hello
  (
    <Card4x5 id="post-5-slide-4" key="s4">
      <p className="font-sans text-[9px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold">Step 3 / 5</p>
      <div className="text-center">
        <p className="font-serif text-7xl text-[#5EEAD4] mb-4">3.</p>
        <h3 className="font-serif text-3xl md:text-4xl mb-3">SAY HELLO.</h3>
        <p className="font-sans text-sm text-white/80 leading-relaxed">
          To yourself. To your Divine Team. To whoever walks with you. <em>Out loud or inside.</em>
        </p>
      </div>
      <BrandFooter small />
    </Card4x5>
  ),
  // Slide 5 — Say Thank You
  (
    <Card4x5 id="post-5-slide-5" key="s5">
      <p className="font-sans text-[9px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold">Step 4 / 5</p>
      <div className="text-center">
        <p className="font-serif text-7xl text-[#5EEAD4] mb-4">4.</p>
        <h3 className="font-serif text-3xl md:text-4xl mb-3">SAY THANK YOU.</h3>
        <p className="font-sans text-sm text-white/80 leading-relaxed">
          For guidance. For protection. For the tiny signs. For being here at all.
        </p>
      </div>
      <BrandFooter small />
    </Card4x5>
  ),
  // Slide 6 — Return
  (
    <Card4x5 id="post-5-slide-6" key="s6">
      <p className="font-sans text-[9px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold">Step 5 / 5</p>
      <div className="text-center">
        <p className="font-serif text-7xl text-[#5EEAD4] mb-4">5.</p>
        <h3 className="font-serif text-3xl md:text-4xl mb-3">RETURN.</h3>
        <p className="font-sans text-sm text-white/80 leading-relaxed">
          Not only in the morning. Throughout the day. As often as you need. <em>The door is always open.</em>
        </p>
      </div>
      <BrandFooter small />
    </Card4x5>
  ),
  // Slide 7 — CTA
  (
    <Card4x5 id="post-5-slide-7" key="s7">
      <div>
        <p className="font-sans text-[9px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold">Last Slide · For You</p>
      </div>
      <div className="text-center">
        <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">
          Free <span className="text-[#5EEAD4]">Yes. No. Wait.</span>
          <br />
          printable cards.
        </h2>
        <p className="font-serif italic text-base text-[#CCFBF1] mb-4">For the moments you cannot hear clearly.</p>
        <div className="inline-block bg-[#5EEAD4] text-[#042F2E] font-bold text-[10px] tracking-wider uppercase px-4 py-2 rounded-full">
          Comment CARDS · Link in Bio
        </div>
      </div>
      <BrandFooter small />
    </Card4x5>
  ),
];

const POSTS = [
  {
    id: "post-1",
    archetype: "🪓 PATTERN INTERRUPT",
    purpose: "Saves + Shares · Stops the scroll",
    format: "1:1 Square · IG Feed + FB",
    cta: "Awareness · Save + tag a friend",
    image: <Post1Image />,
    caption: `If you reach for your phone before you reach for your own heart — this is for you. 💛

The world has trained us to consume before we connect.
To answer before we breathe.
To produce before we are even fully here.

I built a 60-second daily HYGIENE for that exact moment.
It is called Tapped-In. It costs nothing. It takes 60 seconds.

Save this post. Send it to a woman who reaches for her phone first too.

Full 9-min audio essay → link in bio.

— Temple

#mindbodyspirithygiene #morninghygiene #igetto #consciouswomen #realtoolsforrealwomen #spiritualhygiene #mindfulness #morningroutine #anxietyrelief #templefranklin`,
  },
  {
    id: "post-2",
    archetype: "📋 LISTICLE",
    purpose: "Saves + Comments · DM trigger",
    format: "1:1 Square · IG Feed + FB",
    cta: "Lead Magnet · Comment CARDS for free Yes. No. Wait. deck",
    image: <Post2Image />,
    caption: `5 signs you are not Tapped-In:

1. You apologize before you speak
2. Your "no" has 5 sentences attached
3. You wake up exhausted before you stand up
4. You forgot what YOU actually want
5. You are performing your life — not living it

If even ONE of these is you right now — that is not a flaw. That is your nervous system asking to come home.

I made you a free Yes. No. Wait. printable card deck for the moments you cannot hear yourself clearly. Pocket-sized. Yours forever.

💌 Comment CARDS and I will DM you the link.

— Temple

#mindbodyspirithygiene #boundaries #consciouswomen #spiritualhygiene #anxietyrelief #peoplepleaserrecovery #morninghygiene #igetto #templefranklin #selfcare`,
  },
  {
    id: "post-3",
    archetype: "❤️ VULNERABLE STORY",
    purpose: "Comments + DMs · Builds trust",
    format: "1:1 Square · IG Feed + FB",
    cta: "Awareness · Drive to audio essay",
    image: <Post3Image />,
    caption: `"The pillow hears my screams. And wipes my tears."

That was the morning I stopped looking outside myself for the answer.

I am overwhelmed. And someone always needs something from me. How do I get back home to myself again?

That morning I built a 60-second HYGIENE — Tap-In, Ground, Say Hello to my Divine Team, Say Thank You, Return.

It rewired my mornings. It rewired ME.

The full 9-minute audio essay — in my real voice — is on the blog. Link in bio. 💛

— Temple

#mindbodyspirithygiene #realtalk #messymiddle #consciouswomen #spiritualhygiene #templefranklin #morninghygiene #divineteam #reclaiming #igetto`,
  },
  {
    id: "post-4",
    archetype: "🔄 REFRAME",
    purpose: "Saves + Shares · Brand reinforcement",
    format: "1:1 Square · IG Feed + FB + Pinterest",
    cta: "Awareness · Save + share",
    image: <Post4Image />,
    caption: `Stop saying "I have to be productive."

Start LIVING "I GET TO Tap In." 💛

This is the entire premise of Mind Body Spirit HYGIENE™ — three little words that change your relationship with EVERY moment of your day.

You don't HAVE to start the day in survival mode.
You GET TO start it Tapped-In.

Save this. Share with the woman in your life who is running on empty.

Full 60-second practice + free audio → link in bio.

— Temple

#igetto #mindbodyspirithygiene #morninghygiene #mindsetshift #consciouswomen #realtoolsforrealwomen #spiritualhygiene #templefranklin #reframe #intentionalliving`,
  },
  {
    id: "post-5",
    archetype: "🎓 CAROUSEL TEACHING",
    purpose: "Saves (highest viral signal in 2026) + Email signups",
    format: "4:5 Carousel · 7 slides · IG Feed",
    cta: "Lead Magnet · Comment CARDS · Link in Bio",
    image: (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {Post5Slides}
      </div>
    ),
    isCarousel: true,
    caption: `How to come home to yourself in 60 seconds. 💛

Save this carousel for the mornings you forget you have a self to come home to.

This is Tapped-In — the 5-step HYGIENE I built when I was so overwhelmed the pillow heard my screams.

It costs nothing. It takes 60 seconds. You don't have to earn it. You don't have to be good enough for it.

→ Tap In
→ Ground
→ Say Hello
→ Say Thank You
→ Return

The door is always open.

💌 Comment CARDS and I will DM you the free Yes. No. Wait. printable deck — for the moments you cannot hear yourself clearly.

— Temple

#mindbodyspirithygiene #morningroutine #morninghygiene #spiritualhygiene #consciouswomen #realtoolsforrealwomen #igetto #templefranklin #divineteam #grounding #anxietyrelief #selflove #innerguidance #intentionalliving`,
  },
];

export default function ContentBank() {
  useEffect(() => {
    document.title = "Content Bank — Pillar 1: Tapped-In | Temple Franklin";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  return (
    <div className="bg-[#efeae7] min-h-screen pb-16" data-testid="content-bank-page">
      <header className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-[#CCFBF1]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/social"
            className="text-[#25a1c2] hover:text-[#042F2E] text-sm font-medium inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" /> Publish Toolkit
          </Link>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#25a1c2] bg-[#F0F5F4] px-2.5 py-1 rounded-full">
            Content Bank · Private
          </span>
        </div>
      </header>

      <div className="pt-28 px-6">
        <div className="max-w-6xl mx-auto">
          {/* HERO */}
          <div className="text-center mb-12">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#25a1c2] font-bold mb-3">
              Pillar 1 of 10 · Tapped-In
            </p>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#042F2E] mb-4">
              The 5 Test Posts
            </h1>
            <p className="text-[#475569] max-w-2xl mx-auto">
              One of each archetype. All on your strongest pillar (the audio is already live). Right-click each image → save. Click "Copy Caption" → paste in Metricool.
            </p>
          </div>

          {/* INSTRUCTIONS BAR */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-12 text-center text-sm text-amber-900">
            <p className="font-bold mb-1">📋 The 60-second post-prep flow</p>
            <p>1. Right-click the image → "Save Image As..." → Desktop folder · 2. Click "Copy Caption" · 3. Paste both into Metricool · 4. Schedule. Done.</p>
          </div>

          {/* POSTS */}
          <div className="space-y-16">
            {POSTS.map((post, idx) => (
              <section
                key={post.id}
                className="bg-white rounded-2xl border border-[#CCFBF1] shadow-sm overflow-hidden"
                data-testid={`section-${post.id}`}
              >
                {/* TOP META BAR */}
                <div className="bg-[#042F2E] px-6 py-4 flex flex-wrap items-center justify-between gap-3 text-white">
                  <div>
                    <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold">
                      Post {idx + 1} of 5
                    </p>
                    <h2 className="font-serif text-xl text-white">{post.archetype}</h2>
                  </div>
                  <div className="text-right text-xs space-y-1">
                    <p className="text-[#5EEAD4]">{post.format}</p>
                    <p className="text-white/60">{post.purpose}</p>
                  </div>
                </div>

                <div className="p-6 md:p-8">
                  <div className={`grid ${post.isCarousel ? "grid-cols-1" : "md:grid-cols-2"} gap-8`}>
                    {/* IMAGE */}
                    <div>
                      <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#25a1c2] font-bold mb-3">
                        Step 1 · Right-click → Save Image{post.isCarousel ? "s (all 7)" : ""}
                      </p>
                      {post.isCarousel ? post.image : <div className="max-w-[420px]">{post.image}</div>}
                    </div>

                    {/* CAPTION */}
                    <div>
                      <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#25a1c2] font-bold mb-3">
                        Step 2 · Copy Caption
                      </p>
                      <div className="bg-[#efeae7] border border-[#CCFBF1] rounded-2xl p-5 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-sans text-xs font-bold uppercase tracking-widest text-[#25a1c2]">
                            Caption + Hashtags
                          </h3>
                          <CopyBtn value={post.caption} label="Copy" testId={`copy-${post.id}`} />
                        </div>
                        <p
                          className="text-[#042F2E] text-xs leading-relaxed whitespace-pre-line max-h-96 overflow-y-auto"
                          data-testid={`${post.id}-caption-text`}
                        >
                          {post.caption}
                        </p>
                      </div>
                      <div className="bg-[#F0FDF4] border border-[#25a1c2]/20 rounded-xl p-3 text-xs">
                        <p className="text-[#25a1c2] font-bold mb-1">🎯 Goal of this post:</p>
                        <p className="text-[#068fa2]">{post.cta}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            ))}
          </div>

          {/* POSTING SCHEDULE */}
          <div className="mt-16 bg-gradient-to-br from-[#042F2E] to-[#068fa2] rounded-2xl p-8 text-white" data-testid="posting-schedule">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold mb-3">Suggested 5-Day Test Schedule</p>
            <h2 className="font-serif text-3xl mb-6">Your Week of Tapped-In</h2>
            <div className="space-y-3">
              {[
                { day: "Monday", post: "Post 4 · The Reframe", note: "Strong open · brand anchor" },
                { day: "Tuesday", post: "Post 1 · Pattern Interrupt", note: "Save-bait · scroll stopper" },
                { day: "Wednesday", post: "Post 5 · Carousel Teaching", note: "Mid-week save spike · email funnel" },
                { day: "Thursday", post: "Post 2 · Listicle", note: "Comment trigger for DMs" },
                { day: "Friday", post: "Post 3 · Vulnerable Story", note: "Trust + traffic to audio" },
              ].map((row) => (
                <div key={row.day} className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="font-serif text-[#5EEAD4] text-2xl w-28 flex-shrink-0">{row.day}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-sans font-bold text-sm">{row.post}</p>
                    <p className="text-white/60 text-xs">{row.note}</p>
                  </div>
                  <Instagram className="w-4 h-4 text-[#5EEAD4]/60 flex-shrink-0" />
                  <Pin className="w-4 h-4 text-[#5EEAD4]/60 flex-shrink-0" />
                </div>
              ))}
            </div>
            <p className="text-white/70 text-xs italic mt-6 text-center">
              Posts 4 + 1 + 2 also work as Pinterest pins. Just upload the same image with the same caption to Pinterest in Metricool.
            </p>
          </div>

          <p className="text-center text-[10px] text-[#475569] italic mt-10">
            Bookmark this page → mindbodyspirithygienetools.com/content-bank
          </p>
        </div>
      </div>
    </div>
  );
}
